import { fragmentHiddenIdsAtom, fragmentTreeOpenAtom, selectedFragmentElementAtom, selectedLayersAtom } from '@/atoms';
import { getLinkSessionId } from '@/graphql/execute';
import { useFeatureCheck } from '@/hooks/useFeature';
import { useOrganizations } from '@/hooks/useOrganizations';
import { cn } from '@/lib/utils';
import { useDialog } from '@/providers/DialogProvider';
import * as FRAGS from '@thatopen/fragments';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { ChevronDown, ChevronRight, Crosshair, Download, Eye, EyeOff, Loader2 } from 'lucide-react';
import { createContext, memo, useCallback, useContext, useDeferredValue, useEffect, useMemo, useState, type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  collectIds,
  flattenUnnamed,
  idMatchBadge,
  itemName,
  loadSpatialTree,
  nodeMatchesQuery,
  nodeTooltip,
  type ItemInfoMap,
} from './fragmentTreeData';
import { getFragments, jumpToFragment } from './IFC2';
import LandxmlExportDialog, { LANDXML_EXPORT_DIALOG_CLASS } from './LandxmlExportDialog';
import { QuantityTakeoffDialog } from './QuantityTakeoffDialog';
import { Button } from './ui/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from './ui/context-menu';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer';
import { Input } from './ui/input';

const ItemsContext = createContext<ItemInfoMap>(new Map());

/**
 * Search state, precomputed once per (tree, items, query). Each TreeNode
 * does O(1) `has` lookups instead of re-walking its subtree on every render.
 */
interface SearchState {
  /** Lowercased query (empty when no filter is active). */
  query: string;
  /** Nodes whose own name, category or identifier matches `query`. */
  selfMatches: WeakSet<FRAGS.SpatialTreeItem>;
  /** Nodes themselves OR with a matching descendant (= "in scope"). */
  inScope: WeakSet<FRAGS.SpatialTreeItem>;
}

const EMPTY_SEARCH_STATE: SearchState = {
  query: '',
  selfMatches: new WeakSet(),
  inScope: new WeakSet(),
};

const SearchContext = createContext<SearchState>(EMPTY_SEARCH_STATE);

/**
 * Whether the LandXML export entry should appear in a node's context menu.
 * Resolved ONCE in `FragmentSpatialTree` — `useOrganizations()` fires a
 * `myOrganizations` request from its mount effect, so calling it per TreeNode
 * would issue one network request per row of a (non-virtualized) tree.
 */
const ExportContext = createContext(false);

/**
 * Single post-order walk that fills both selfMatches and inScope. Empty
 * string short-circuits — no allocations in the no-filter case.
 */
function buildSearchState(root: FRAGS.SpatialTreeItem | null, items: ItemInfoMap, q: string): SearchState {
  if (!q || !root) return EMPTY_SEARCH_STATE;
  const selfMatches = new WeakSet<FRAGS.SpatialTreeItem>();
  const inScope = new WeakSet<FRAGS.SpatialTreeItem>();

  const visit = (node: FRAGS.SpatialTreeItem): boolean => {
    let any = nodeMatchesQuery(node, items, q);
    if (any) selfMatches.add(node);
    if (node.children) {
      for (const c of node.children) {
        if (visit(c)) any = true;
      }
    }
    if (any) inScope.add(node);
    return any;
  };
  visit(root);
  return { query: q, selfMatches, inScope };
}

/**
 * Wrap occurrences of `q` (case-insensitive) inside `text` with a <mark>.
 * Used to highlight which substring satisfied a search match.
 */
function highlightMatch(text: string, q: string): React.ReactNode {
  if (!q) return text;
  const lower = text.toLowerCase();
  const parts: React.ReactNode[] = [];
  let i = 0;
  let matchIdx = 0;
  while (i < text.length) {
    const idx = lower.indexOf(q, i);
    if (idx === -1) {
      parts.push(text.slice(i));
      break;
    }
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(
      <mark
        key={matchIdx++}
        className='bg-yellow-200 text-inherit dark:bg-yellow-500/40 rounded-sm px-0.5'>
        {text.slice(idx, idx + q.length)}
      </mark>,
    );
    i = idx + q.length;
  }
  return <>{parts}</>;
}

/** Walk the spatial tree and log every node where category is null/undefined. */
function logUnnamedNodes(modelId: string, root: FRAGS.SpatialTreeItem, items: ItemInfoMap) {
  const rows: Array<{
    path: string;
    localId: number | null;
    name: string;
    childCount: number;
    childCategories: string;
  }> = [];

  const walk = (node: FRAGS.SpatialTreeItem, parentPath: string) => {
    const here = node.category ?? '?';
    const path = parentPath ? `${parentPath} › ${here}` : here;
    if (node.category == null) {
      const childCats = (node.children ?? [])
        .map((c) => c.category ?? '?')
        .reduce<Record<string, number>>((acc, cat) => {
          acc[cat] = (acc[cat] ?? 0) + 1;
          return acc;
        }, {});
      const name = itemName(items, node.localId) ?? '';
      rows.push({
        path,
        localId: node.localId,
        name,
        childCount: node.children?.length ?? 0,
        childCategories: Object.entries(childCats)
          .map(([k, v]) => `${k}×${v}`)
          .join(', '),
      });
    }
    for (const c of node.children ?? []) walk(c, path);
  };
  walk(root, '');

  if (rows.length === 0) return;
  console.groupCollapsed(`[FragmentSpatialTree] ${modelId} — ${rows.length} unnamed group(s)`);
  console.table(rows);
  console.log('Raw tree:', root);
  console.groupEnd();
}

interface TreeNodeProps {
  node: FRAGS.SpatialTreeItem;
  modelId: string;
  depth: number;
}

const TreeNode = memo(({ node, modelId, depth }: TreeNodeProps) => {
  const [open, setOpen] = useState(depth < 2);
  const [hiddenIds, setHiddenIds] = useAtom(fragmentHiddenIdsAtom(modelId));
  const { t } = useTranslation('components');
  const dialog = useDialog();
  // `useFeatureCheck` is a pure atom read (no mount effect) — safe per node.
  // `canExport` comes from context; see ExportContext.
  const featureCheck = useFeatureCheck();
  const canExport = useContext(ExportContext);
  const { projectId } = useParams();
  // Lazily read the layer list at click time (in `exportLandxml`) via the
  // jotai store instead of subscribing every TreeNode to
  // `selectedLayersAtom` — that atom returns a fresh array on every layer
  // change, and this tree is not virtualized, so a per-node subscription
  // would re-render the whole tree on every layer edit.
  const store = useStore();

  const items = useContext(ItemsContext);
  const search = useContext(SearchContext);
  const query = search.query;
  const setSelectedFragment = useSetAtom(selectedFragmentElementAtom);
  const subtreeIds = useMemo(() => collectIds(node), [node]);
  // Flatten unnamed grouping containers so they don't show up as "?" rows.
  // Visibility still operates on the raw subtree (collectIds), so nothing leaks.
  const flat = useMemo(() => flattenUnnamed(node.children, items), [node.children, items]);
  const selfMatches = query ? search.selfMatches.has(node) : false;
  // Searching: when this node itself matches, show its full subtree (the
  // user found it, so let them browse inside). When only descendants match,
  // collapse to the path that leads to the match.
  const displayChildren = useMemo(() => {
    if (!query) return flat;
    if (selfMatches) return flat;
    return flat.filter((c) => search.inScope.has(c));
  }, [flat, query, selfMatches, search]);
  const hasChildren = displayChildren.length > 0;
  const effectiveOpen = query ? true : open;
  const name = itemName(items, node.localId);

  const allHidden = useMemo(
    () => subtreeIds.length > 0 && subtreeIds.every((id) => hiddenIds.has(id)),
    [subtreeIds, hiddenIds],
  );
  const someHidden = useMemo(
    () => !allHidden && subtreeIds.some((id) => hiddenIds.has(id)),
    [allHidden, subtreeIds, hiddenIds],
  );

  const toggleVisible = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const fragments = await getFragments();
      const model = fragments.models.list.get(modelId);
      if (!model) return;
      const shouldShow = allHidden || someHidden;
      await model.setVisible(subtreeIds, shouldShow);
      setHiddenIds((prev) => {
        const next = new Set(prev);
        for (const id of subtreeIds) {
          if (shouldShow) next.delete(id);
          else next.add(id);
        }
        return next;
      });
    },
    [allHidden, someHidden, subtreeIds, modelId, setHiddenIds],
  );

  const selectThis = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (node.localId == null) return;
      const layerId = modelId.replace(/^frag-/, '');
      setSelectedFragment({
        localId: node.localId,
        modelId,
        layerId,
        properties: [],
        loading: true,
      });
    },
    [node.localId, modelId, setSelectedFragment],
  );

  const isolate = useCallback(async () => {
    const fragments = await getFragments();
    const model = fragments.models.list.get(modelId);
    if (!model) return;
    // Hide everything, then re-show this subtree.
    await model.setVisible(undefined, false);
    await model.setVisible(subtreeIds, true);
    const allIds = await model.getLocalIds();
    const subtreeSet = new Set(subtreeIds);
    setHiddenIds(new Set(allIds.filter((id) => !subtreeSet.has(id))));
  }, [modelId, subtreeIds, setHiddenIds]);

  const hideSubtree = useCallback(async () => {
    const fragments = await getFragments();
    const model = fragments.models.list.get(modelId);
    if (!model) return;
    await model.setVisible(subtreeIds, false);
    setHiddenIds((prev) => {
      const next = new Set(prev);
      for (const id of subtreeIds) next.add(id);
      return next;
    });
  }, [modelId, subtreeIds, setHiddenIds]);

  const showAll = useCallback(async () => {
    const fragments = await getFragments();
    const model = fragments.models.list.get(modelId);
    if (!model) return;
    await model.setVisible(undefined, true);
    setHiddenIds(new Set());
  }, [modelId, setHiddenIds]);

  const jumpTo = useCallback(() => {
    if (node.localId == null) return;
    jumpToFragment(modelId, node.localId);
  }, [modelId, node.localId]);

  const copyName = useCallback(async () => {
    const text = name ?? node.category ?? '';
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('fragmentTree.copied'));
    } catch {
      toast.error(t('fragmentTree.copyFailed'));
    }
  }, [name, node.category, t]);

  const copyGuid = useCallback(async () => {
    if (node.localId == null) return;
    try {
      const fragments = await getFragments();
      const model = fragments.models.list.get(modelId);
      if (!model) return;
      const datas = await model.getItemsData([node.localId], {
        attributesDefault: true,
        relationsDefault: { attributes: false, relations: false },
      });
      const guid = (datas?.[0]?._guid as FRAGS.ItemAttribute | undefined)?.value;
      if (typeof guid === 'string' && guid) {
        await navigator.clipboard.writeText(guid);
        toast.success(t('fragmentTree.copied'));
      } else {
        toast.error(t('fragmentTree.noGuid'));
      }
    } catch {
      toast.error(t('fragmentTree.copyFailed'));
    }
  }, [modelId, node.localId, t]);

  const showQuantities = useCallback(() => {
    dialog.openDialog(
      <QuantityTakeoffDialog modelId={modelId} localIds={subtreeIds} title={name ?? node.category ?? ''} />,
    );
  }, [dialog, modelId, subtreeIds, name, node.category]);

  const exportLandxml = useCallback(() => {
    const gate = featureCheck('export.landxml');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    const layerId = modelId.replace(/^frag-/, '');
    const layers = store.get(selectedLayersAtom(projectId ?? ''));
    const layerName = layers.find((l) => l.id === layerId)?.name ?? name ?? node.category ?? 'IFC';
    dialog.openDialog(
      <LandxmlExportDialog
        projectId={projectId!}
        layerId={layerId}
        layerName={layerName}
        initialLocalIds={subtreeIds}
      />,
      LANDXML_EXPORT_DIALOG_CLASS,
    );
  }, [featureCheck, modelId, store, projectId, name, node.category, dialog, subtreeIds]);

  const cat = node.category ?? undefined;
  const label = name && cat
    ? `${name} (${cat})`
    : name
      ? name
      : cat && !hasChildren && node.localId != null
        ? `${cat} #${node.localId}`
        : cat ?? '?';
  // Same label, but with <mark>'d substrings when there's an active query.
  const labelNode: React.ReactNode = !query
    ? label
    : name && cat
      ? <>{highlightMatch(name, query)} ({highlightMatch(cat, query)})</>
      : name
        ? highlightMatch(name, query)
        : cat && !hasChildren && node.localId != null
          ? <>{highlightMatch(cat, query)} #{node.localId}</>
          : cat
            ? highlightMatch(cat, query)
            : '?';
  // When the row is only in the result list because of an identifier (Tag,
  // express id or GUID), show that identifier so the match is explainable.
  const idBadge = query ? idMatchBadge(node, items, query) : undefined;
  const title = nodeTooltip(label, node, items);

  return (
    <div className='select-none'>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div className='flex items-center gap-1 px-1 py-0.5 rounded hover:bg-slate-100 dark:hover:bg-stone-800/60'>
            <button
              type='button'
              onClick={() => hasChildren && setOpen((o) => !o)}
              className='size-4 flex items-center justify-center text-slate-400'
              aria-label='toggle'>
              {hasChildren ? (
                effectiveOpen ? <ChevronDown className='size-3' /> : <ChevronRight className='size-3' />
              ) : null}
            </button>
            <button
              type='button'
              onClick={toggleVisible}
              className='size-5 flex items-center justify-center'
              title={allHidden ? t('fragmentTree.show') : t('fragmentTree.hide')}>
              {allHidden ? (
                <EyeOff className='size-3.5 text-slate-400' />
              ) : (
                <Eye className={cn('size-3.5', someHidden && 'opacity-40')} />
              )}
            </button>
            <button
              type='button'
              onClick={selectThis}
              disabled={node.localId == null}
              className={cn(
                'text-xs flex-1 truncate font-mono text-left hover:underline disabled:cursor-default disabled:no-underline',
                selfMatches && 'font-semibold',
              )}
              title={title}>
              {labelNode}
            </button>
            {idBadge && (
              <span className='ml-1 shrink-0 text-[10px] font-mono text-slate-400 truncate max-w-24' title={idBadge}>
                {idBadge}
              </span>
            )}
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onClick={jumpTo} disabled={node.localId == null}>
            <Crosshair className='size-4' />
            {t('fragmentTree.menu.jumpTo')}
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={isolate}>{t('fragmentTree.menu.isolate')}</ContextMenuItem>
          <ContextMenuItem onClick={hideSubtree}>{t('fragmentTree.menu.hide')}</ContextMenuItem>
          <ContextMenuItem onClick={showAll}>{t('fragmentTree.menu.showAll')}</ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={showQuantities}>{t('fragmentTree.menu.showQuantities')}</ContextMenuItem>
          {canExport && (
            <ContextMenuItem onClick={exportLandxml}>
              <Download className='size-4' />
              {t('fragmentTree.menu.exportLandxml')}
            </ContextMenuItem>
          )}
          <ContextMenuSeparator />
          <ContextMenuItem onClick={copyName} disabled={!name && !node.category}>
            {t('fragmentTree.menu.copyName')}
          </ContextMenuItem>
          <ContextMenuItem onClick={copyGuid} disabled={node.localId == null}>
            {t('fragmentTree.menu.copyGuid')}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
      {effectiveOpen && hasChildren && (
        <div className='pl-3 border-l border-slate-200/70 dark:border-stone-700/40 ml-2'>
          {displayChildren.map((child, i) => (
            <TreeNode
              key={`${child.category ?? ''}-${child.localId}-${i}`}
              node={child}
              modelId={modelId}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
});
TreeNode.displayName = 'TreeNode';

/**
 * Renders the root of one model's tree, applying the search filter at the
 * top level too. If the root is itself a bare grouping node (no category,
 * no name), its flattened children become the new top level.
 */
const RootRender: FunctionComponent<{
  tree: FRAGS.SpatialTreeItem;
  modelId: string;
  items: ItemInfoMap;
}> = ({ tree, modelId, items }) => {
  const search = useContext(SearchContext);
  const query = search.query;
  const isUnnamedRoot = tree.category == null && itemName(items, tree.localId) === undefined;

  if (isUnnamedRoot) {
    let children = flattenUnnamed(tree.children, items);
    if (query) children = children.filter((c) => search.inScope.has(c));
    return (
      <>
        {children.map((child, i) => (
          <TreeNode
            key={`${child.category ?? ''}-${child.localId}-${i}`}
            node={child}
            modelId={modelId}
            depth={0}
          />
        ))}
      </>
    );
  }
  if (query && !search.inScope.has(tree)) return null;
  return <TreeNode node={tree} modelId={modelId} depth={0} />;
};

const LayerTree: FunctionComponent<{ layerId: string; layerName: string; query: string }> = ({ layerId, layerName, query }) => {
  const modelId = `frag-${layerId}`;
  const [tree, setTree] = useState<FRAGS.SpatialTreeItem | null>(null);
  const [items, setItems] = useState<ItemInfoMap>(new Map());
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('components');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fragments = await getFragments();
        // The model loads asynchronously in IFC2; poll briefly until it's ready.
        let model = fragments.models.list.get(modelId);
        let waited = 0;
        while (!model && waited < 30000) {
          await new Promise((r) => setTimeout(r, 250));
          if (cancelled) return;
          model = fragments.models.list.get(modelId);
          waited += 250;
        }
        if (!model) {
          setError(t('fragmentTree.modelNotLoaded'));
          return;
        }
        // Shared with the LandXML export dialog's picker (fragmentTreeData) so
        // both show identical labels and flattening. The tree is handed over
        // via `onTree` before the (slow, chunked) name fetch finishes.
        const { tree: structure, items: itemMap } = await loadSpatialTree(model, {
          onTree: (partialTree) => {
            if (!cancelled) setTree(partialTree);
          },
          isCancelled: () => cancelled,
        });
        if (cancelled) return;
        setItems(itemMap);
        logUnnamedNodes(modelId, structure, itemMap);
      } catch (err) {
        if (!cancelled) setError(String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId, t]);

  return (
    <div className='flex flex-col gap-1'>
      <div className='text-xs font-medium text-muted-foreground px-1'>{layerName}</div>
      {error ? (
        <div className='text-xs text-red-500 px-1'>{error}</div>
      ) : !tree ? (
        <div className='flex items-center gap-2 text-xs text-muted-foreground px-1'>
          <Loader2 className='size-3 animate-spin' />
          {t('fragmentTree.loading')}
        </div>
      ) : (
        <LayerTreeBody tree={tree} items={items} modelId={modelId} query={query} />
      )}
    </div>
  );
};

/**
 * Renders one model's tree. Builds the search state in a single useMemo
 * keyed on (tree, items, query) so the cost is paid once per query change
 * — every TreeNode below it then does O(1) `WeakSet.has` lookups.
 */
const LayerTreeBody: FunctionComponent<{
  tree: FRAGS.SpatialTreeItem;
  items: ItemInfoMap;
  modelId: string;
  query: string;
}> = ({ tree, items, modelId, query }) => {
  const search = useMemo(() => buildSearchState(tree, items, query), [tree, items, query]);
  return (
    <ItemsContext.Provider value={items}>
      <SearchContext.Provider value={search}>
        <RootRender tree={tree} modelId={modelId} items={items} />
      </SearchContext.Provider>
    </ItemsContext.Provider>
  );
};

const FragmentSpatialTree: FunctionComponent = () => {
  const { projectId } = useParams();
  const [open, setOpen] = useAtom(fragmentTreeOpenAtom);
  const layers = useAtomValue(selectedLayersAtom(projectId!));
  const { t } = useTranslation('components');
  // Single `useOrganizations()` call for the whole tree (it fetches on mount).
  const { hasRole } = useOrganizations();
  const canExport = hasRole('EDITOR') && !getLinkSessionId();
  const [query, setQuery] = useState('');
  // Defer the query so the tree filter never blocks keystrokes — typing
  // updates `query` immediately (input stays responsive) while React
  // schedules the tree-filter render at lower priority.
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());

  const fragmentLayers = useMemo(
    () => layers.filter((l) => l.type === 'MODEL' && (l.metadata as any)?.type === 'FRAGMENT'),
    [layers],
  );

  if (fragmentLayers.length === 0) return null;

  return (
    <ExportContext.Provider value={canExport}>
      <Drawer open={open} onClose={() => setOpen(false)} direction='left'>
        <DrawerContent>
          <DrawerHeader className='w-md'>
            <DrawerTitle>{t('fragmentTree.title')}</DrawerTitle>
          </DrawerHeader>
          <div className='px-3'>
            <Input
              placeholder={t('fragmentTree.searchPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className='h-8 text-xs'
            />
          </div>
          <div className='flex-1 overflow-y-auto px-3 py-2'>
            <div className='flex flex-col gap-4'>
              {fragmentLayers.map((layer) => (
                <LayerTree key={layer.id} layerId={layer.id} layerName={layer.name} query={deferredQuery} />
              ))}
            </div>
          </div>
          <DrawerFooter>
            <Button variant='link' onClick={() => setOpen(false)}>
              {t('fragmentTree.close')}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </ExportContext.Provider>
  );
};

export default FragmentSpatialTree;
